import { DateTimeStringFormat } from '../../../constants/time-formats';
import { PostOnDateObject, ScheduledDateForm, ScheduledDateTimesType } from '../../../models/post-editor';
import { convertAmPmTo24h } from '../../../helpers/timeFormatter';
import { ImmutabilityContext } from '../states/effects/customImmutabilityContext';
import { format, isBefore, parse, set } from 'date-fns';
import { TimezoneSetting } from '../../../models/graphql';
import { formatInTimeZone, toDate } from 'date-fns-tz';
import { TimezoneMap, TimezoneNames } from '../../../constants/timezones';
import { cloneDeep } from 'lodash';

const sortScheduledDateTimes: (a: ScheduledDateTimesType, b: ScheduledDateTimesType) => number = (a, b) => {
  if (a.dateTime > b.dateTime) {
    return 1;
  } else if (a.dateTime < b.dateTime) {
    if (!a.isPast && b.isPast) {
      return 1;
    }
    return -1;
  } else if (!a.isPast && b.isPast) {
    return 1;
  }
  return 0;
};
const addAndSortScheduledDates = (dates: ScheduledDateTimesType[], date: Date): ScheduledDateTimesType[] => {
  const { update: updateImmutable } = ImmutabilityContext;
  const dateStr = format(date, DateTimeStringFormat);
  const dateStrExists = !!dates.find(d => d.dateTime === dateStr);
  return dateStrExists
    ? dates
    : updateImmutable(dates, { $push: [{ dateTime: dateStr, isPast: false }] }).sort(sortScheduledDateTimes);
};

const checkIfScheduledPostIsPast = (date: ScheduledDateTimesType['dateTime'], timeZone: TimezoneSetting): boolean => {
  const timeZoneName = TimezoneMap[timeZone];
  const parsedDateZoned = toDate(date, { timeZone: timeZoneName });
  return isBefore(parsedDateZoned, new Date());
};

const convertUtcToZonedFormattedDate = (
  date: ScheduledDateTimesType['dateTime'],
  timezone: TimezoneSetting
): string => {
  const utcZoneName = TimezoneMap[TimezoneNames['UTC']];
  const timeZoneName = TimezoneMap[timezone];
  const parsedDateZoned = toDate(date, { timeZone: utcZoneName });
  return formatInTimeZone(parsedDateZoned, timeZoneName, DateTimeStringFormat);
};

const convertIsoStringDateToPostOnDate = (isoDate: string): Omit<PostOnDateObject, 'isPast'> => {
  const parsedDate = parse(isoDate, DateTimeStringFormat, new Date());
  const hours = parsedDate.getHours();
  const minutes = parsedDate.getMinutes();
  const date = set(parsedDate, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
  const time = set(parsedDate, { hours, minutes, seconds: 0, milliseconds: 0 });
  return {
    date,
    time
  };
};

const convertPostOnDateObjectToIsoString = (date: PostOnDateObject): string => {
  const { date: postOnDate, time } = date;
  const minutes = time.getMinutes();
  const hour = time.getHours();
  const newDate = new Date(postOnDate).setHours(hour, minutes);
  return new Date(newDate).toISOString();
};

const convertScheduledDateToPostOnDate = (date: ScheduledDateForm, isPast = false): PostOnDateObject => {
  const minutes = +date.minute;
  const hour = date.ampm ? +convertAmPmTo24h(date.hour, date.ampm) : +date.hour;
  const time = new Date().setHours(hour, minutes, 0, 0);
  return {
    date: date.date,
    time: new Date(time),
    isPast
  };
};

const getAllIndexes = (length: number): number[] => {
  let result: number[] = [];
  for (let i = 0; i < length; i++) {
    result = [...result, i];
  }
  return result;
};

const updateAndSortScheduledDates = (
  dates: ScheduledDateTimesType[],
  date: Date,
  index: number
): ScheduledDateTimesType[] => {
  const { update: updateImmutable } = ImmutabilityContext;
  const newDate = format(date, DateTimeStringFormat);
  let copiedDates = cloneDeep(dates);
  const dateStrExists = !!copiedDates.find(d => d.dateTime === newDate);
  if (dateStrExists) {
    copiedDates = updateImmutable(dates, { $splice: [[index, 1]] });
  } else {
    copiedDates = updateImmutable(dates, { [index]: { $set: { dateTime: newDate, isPast: false } } });
  }

  copiedDates.sort(sortScheduledDateTimes);
  return copiedDates;
};

export const ScheduleHelpers = {
  sortScheduledDateTimes,
  addAndSortScheduledDates,
  checkIfScheduledPostIsPast,
  convertIsoStringDateToPostOnDate,
  convertPostOnDateObjectToIsoString,
  convertScheduledDateToPostOnDate,
  convertUtcToZonedFormattedDate,
  getAllIndexes,
  updateAndSortScheduledDates
};
